/**
 * itemChanged — the pane's ONE subscription to Outlook's `ItemChanged`.
 *
 * Outlook cannot remove a single event handler. `mailbox.removeHandlerAsync`
 * takes no handler: it removes EVERY ItemChanged handler the pane holds, and a
 * function passed second is read as its completion callback. (Checked
 * 2026-10-08 against Microsoft's hosted Office.js: `RemoveEventHandler` reads
 * the handler from an options object that defaults to null, and null means
 * `clearEventHandlers`.) Office.js also adds the FIRST handler for an event
 * only after the host confirms the registration, so add → remove-all → add in
 * one tick can leave the wrong handler registered, or none.
 *
 * `useMailboxItem` and `useSharedMailboxTarget` each used to register their own
 * handler and remove it on cleanup, so either hook's cleanup unsubscribed BOTH:
 * React StrictMode's dev double-mount, or a hot reload of App. The "email you
 * are on" strip then stopped following the selection (story 28633).
 *
 * So this module registers with Outlook ONCE, on the first subscriber, and
 * never unregisters. Hooks subscribe HERE, never to Office directly. An
 * unsubscribe only leaves the fan-out set; a handler with nobody listening
 * costs nothing.
 */

type ItemChangedListener = () => void;

const listeners = new Set<ItemChangedListener>();
let registration: "none" | "pending" | "registered" = "none";

function dispatch(): void {
  // A copy: a listener may unsubscribe while the others run.
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch (err) {
      console.error("[itemChanged] a listener threw", err);
    }
  }
}

function ensureRegistered(): void {
  if (registration !== "none") return;
  let mailbox: Office.Mailbox | undefined;
  let eventType: Office.EventType | undefined;
  try {
    eventType = Office?.EventType?.ItemChanged;
    mailbox = Office?.context?.mailbox;
  } catch {
    // No Office global: a plain browser preview, or a test.
    return;
  }
  if (!eventType || typeof mailbox?.addHandlerAsync !== "function") return;

  registration = "pending";
  try {
    mailbox.addHandlerAsync(eventType, dispatch, (result) => {
      if (result?.status === Office.AsyncResultStatus.Failed) {
        // Said out loud: a silent failure here is a pane that never follows
        // the selection. The next subscriber tries again.
        registration = "none";
        console.warn("[itemChanged] Outlook refused the ItemChanged handler", result.error?.message);
        return;
      }
      registration = "registered";
    });
  } catch (err) {
    registration = "none";
    console.warn("[itemChanged] addHandlerAsync threw", err);
  }
}

/**
 * Call `listener` whenever the AM selects a different item while the pane is
 * pinned. Returns the unsubscribe. Safe without an Office host (no-op).
 */
export function subscribeItemChanged(listener: ItemChangedListener): () => void {
  listeners.add(listener);
  ensureRegistered();
  return () => {
    listeners.delete(listener);
  };
}

/** Tests only: forget the listeners and the Outlook registration. */
export function _resetItemChangedForTests(): void {
  listeners.clear();
  registration = "none";
}
